import React, { PropsWithChildren, useEffect } from 'react';
import { Input, Space, Select, Checkbox, Row, Col, Tooltip } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { SearchOutlined } from '@ant-design/icons';

import { DatePicker, SliderWithNumericInputs } from 'components';
import { getInputSuffixFormatter } from 'helpers/label.formatter';
import { PLATE_FIELDS } from 'app-constants/plate-fields';
import { DATE_FORMAT, RANGES } from 'app-constants/date';

import { ContentFilterProps } from 'hoc/withFilter';
import { ComparativeAnalysisFilterValues } from 'store/wim/comparative-analysis/type';

import { OPTION_KEYS_FOR_SELECT } from 'app-constants/select-options';
import { VIOLATIONS_TYPES } from 'app-constants/violations';
import { WAGA_VEHICLE_TYPES } from 'app-constants/waga-vehicle-types';
import { requestGetLaneTypes } from 'store/admin/platforms/wimLanes/slice';
import { useDispatch } from 'react-redux';
import { useGetWimVehicleClassifications } from 'pages/Transits/hooks/use-get-vehicle-classifications';
import { VEHICLE_CONDITION } from 'store/wim/constants';
import { StyledSelect, StyledSelectVehicleTypeItem, StyledTag, TooltipContentWrapper } from './styles';
import { useComparativeAnalysisFilter } from './hooks/use-comparative-analysis-filter';

const { Option } = Select;

function ComparativeAnalysisFilterPanel<ComparativeAnalysisType extends ComparativeAnalysisFilterValues>({
  filterManager,
}: PropsWithChildren<ContentFilterProps<ComparativeAnalysisType>>): JSX.Element {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  useGetWimVehicleClassifications({ filter: VEHICLE_CONDITION.Used });

  const {
    state,
    handleFilterChange,
    platforms,
    onPlatformNameChange,
    onRangePickerChange,
    getOnPlateChange,
    getOnSliderChange,
    metrics,
    lanes,
    onClassificationTypesChange,
    onLanesChange,
    onViolationsTypesChange,
    onTransitErrorChange,
  } = useComparativeAnalysisFilter(filterManager);

  const ranges = {
    [t('date.month')]: RANGES.getMonthRange(),
    [t('date.year')]: RANGES.getYearRange(),
    [t('date.quarter')]: RANGES.getQuarterRange(),
  };

  useEffect(() => {
    dispatch(requestGetLaneTypes());
  }, []);

  return (
    <Space direction="vertical">
      <Row>
        <Col flex="stretch" style={{ width: 352 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <DatePicker.RangePicker
              clearIcon={false}
              value={[dayjs(state.from), dayjs(state.to)]}
              showTime={{ format: DATE_FORMAT.RANGE_PICKER_TIME }}
              format={DATE_FORMAT.RANGE_PICKER}
              ranges={ranges as Record<string, [Dayjs | null, Dayjs | null]>}
              onChange={onRangePickerChange}
            />
          </Space>
        </Col>
      </Row>

      <Row>
        <Space>
          <StyledSelect
            allowClear
            value={state.baseWim}
            showSearch
            placeholder={t('filter.baseWim')}
            optionFilterProp="children"
            onChange={(value) => handleFilterChange('baseWim', value)}
          >
            {platforms
              ?.filter((platform) => !state.intermediateWims?.includes(platform.uid) && platform.uid !== state.endWim)
              .map((platform) => (
                <Option key={platform.uid} value={platform.uid}>
                  {platform.wim}
                </Option>
              ))}
          </StyledSelect>
          <StyledSelect
            maxTagTextLength={16}
            maxTagCount={1}
            allowClear
            value={state.intermediateWims}
            showSearch
            placeholder={t('filter.intermediateWims')}
            optionFilterProp="children"
            onChange={onPlatformNameChange}
            mode="multiple"
          >
            {platforms
              ?.filter((platform) => platform.uid !== state.baseWim && platform.uid !== state.endWim)
              .map((platform) => (
                <Option key={platform.uid} value={platform.uid}>
                  {platform.wim}
                </Option>
              ))}
          </StyledSelect>
          <StyledSelect
            allowClear
            value={state.endWim}
            showSearch
            placeholder={t('filter.endWim')}
            optionFilterProp="children"
            onChange={(value) => handleFilterChange('endWim', value)}
          >
            {platforms
              ?.filter((platform) => !state.intermediateWims?.includes(platform.uid) && platform.uid !== state.baseWim)
              .map((platform) => (
                <Option key={platform.uid} value={platform.uid}>
                  {platform.wim}
                </Option>
              ))}
          </StyledSelect>
        </Space>
      </Row>
      <Row>
        <Space>
          <Col>
            <Space direction="vertical" style={{ width: 280 }}>
              <StyledSelect
                value={state.violationsTypesByOr}
                maxTagCount={1}
                allowClear
                mode="multiple"
                placeholder={t('filter.allOffence')}
                onChange={onViolationsTypesChange}
                showSearch
              >
                <Option key={OPTION_KEYS_FOR_SELECT.All} value={OPTION_KEYS_FOR_SELECT.All}>
                  {t('filter.selectAll')}
                </Option>
                {Object.values(VIOLATIONS_TYPES).map((value) => (
                  <Option key={value.id} value={value.id}>
                    <div>{value.name}</div>
                  </Option>
                ))}
              </StyledSelect>

              <StyledSelect
                mode="multiple"
                maxTagCount={1}
                value={state.vehicleClasses}
                onChange={onClassificationTypesChange}
                placeholder={t('filter.allVehicleTypes')}
                tagRender={(props) => (
                  <StyledTag closable={props.closable} onClose={props.onClose}>
                    <img
                      src={WAGA_VEHICLE_TYPES[props.value as string]?.icon}
                      alt={WAGA_VEHICLE_TYPES[props.value as string]?.description}
                      width={40}
                      height={20}
                    />
                  </StyledTag>
                )}
                allowClear
              >
                <Option key={OPTION_KEYS_FOR_SELECT.All} value={OPTION_KEYS_FOR_SELECT.All}>
                  {t('filter.selectAll')}
                </Option>
                {Object.keys(WAGA_VEHICLE_TYPES).map((wagaId) => (
                  <Option
                    key={`vehicleType-${WAGA_VEHICLE_TYPES[wagaId].description}`}
                    value={WAGA_VEHICLE_TYPES[wagaId].id}
                  >
                    <StyledSelectVehicleTypeItem>
                      <span>{WAGA_VEHICLE_TYPES[wagaId].id}. </span>
                      <img
                        src={WAGA_VEHICLE_TYPES[wagaId].icon}
                        alt={WAGA_VEHICLE_TYPES[wagaId].description}
                        width={50}
                        height={25}
                      />
                      <span>{WAGA_VEHICLE_TYPES[wagaId].description}</span>
                    </StyledSelectVehicleTypeItem>
                  </Option>
                ))}
              </StyledSelect>
            </Space>
          </Col>
          <Col>
            <Space direction="vertical" style={{ width: 280 }}>
              <StyledSelect
                value={state.laneIds}
                maxTagCount={1}
                allowClear
                mode="multiple"
                placeholder={t('filter.allLanes')}
                onChange={onLanesChange}
                showSearch
              >
                <Option value="all">{t('filter.selectAll')}</Option>

                {lanes.map((lane: string) => (
                  <Option key={lane} value={lane}>
                    <div>{lane}</div>
                  </Option>
                ))}
              </StyledSelect>
              <StyledSelect
                value={state.measurementFlags}
                maxTagCount={1}
                allowClear
                mode="multiple"
                placeholder={t('filter.incorrectTransitError')}
                onChange={onTransitErrorChange}
                showSearch
              >
                <Option value="all">{t('filter.selectAll')}</Option>
                {Object.values(metrics).map((metric) => (
                  <Option key={metric?.id} value={metric?.filters?.measurementFlags?.[0] ?? 0}>
                    <div>{t(`measurementFlags.${metric.codeName}` as any)}</div>
                  </Option>
                ))}
              </StyledSelect>
            </Space>
          </Col>

          <Col>
            <Space direction="vertical">
              <Tooltip
                placement="bottom"
                title={<TooltipContentWrapper>{t('filter.plateHint')}</TooltipContentWrapper>}
              >
                <Input
                  value={state.plate}
                  onChange={getOnPlateChange(PLATE_FIELDS.Plate)}
                  placeholder={t('filter.plateFront')}
                  allowClear
                  suffix={<SearchOutlined />}
                />
              </Tooltip>

              <Tooltip
                placement="bottom"
                title={<TooltipContentWrapper>{t('filter.plateHint')}</TooltipContentWrapper>}
              >
                <Input
                  // TODO: enable when API will be ready
                  value={state.plateBack}
                  onChange={getOnPlateChange(PLATE_FIELDS.PlateBack)}
                  placeholder={t('filter.plateBack')}
                  allowClear
                  suffix={<SearchOutlined />}
                />
              </Tooltip>
            </Space>
          </Col>
        </Space>
      </Row>
      <Row>
        <Space>
          <SliderWithNumericInputs
            title={t('filter.axles')}
            max={99}
            startValue={state.axleCountFrom}
            endValue={state.axleCountTo}
            onStartChange={(value) => handleFilterChange('axleCountFrom', value)}
            onEndChange={(value) => handleFilterChange('axleCountTo', value)}
            onSliderChange={getOnSliderChange('axleCountFrom', 'axleCountTo')}
          />
          <SliderWithNumericInputs
            title={t('filter.weight')}
            max={999999}
            startValue={state.weightFrom}
            endValue={state.weightTo}
            onStartChange={(value) => handleFilterChange('weightFrom', value)}
            onEndChange={(value) => handleFilterChange('weightTo', value)}
            onSliderChange={getOnSliderChange('weightFrom', 'weightTo')}
            formatter={getInputSuffixFormatter(t('glossary.measureKilogram'))}
          />
          <SliderWithNumericInputs
            title={t('filter.length')}
            max={99}
            startValue={state.lengthFrom}
            endValue={state.lengthTo}
            onStartChange={(value) => handleFilterChange('lengthFrom', value)}
            onEndChange={(value) => handleFilterChange('lengthTo', value)}
            onSliderChange={getOnSliderChange('lengthFrom', 'lengthTo')}
            formatter={getInputSuffixFormatter(t('glossary.measureMeter'))}
          />
          <SliderWithNumericInputs
            title={t('filter.width')}
            max={99}
            startValue={state.widthFrom}
            endValue={state.widthTo}
            onStartChange={(value) => handleFilterChange('widthFrom', value)}
            onEndChange={(value) => handleFilterChange('widthTo', value)}
            onSliderChange={getOnSliderChange('widthFrom', 'widthTo')}
            formatter={getInputSuffixFormatter(t('glossary.measureMeter'))}
          />
          <SliderWithNumericInputs
            title={t('filter.height')}
            max={99}
            startValue={state.heightFrom}
            endValue={state.heightTo}
            onStartChange={(value) => handleFilterChange('heightFrom', value)}
            onEndChange={(value) => handleFilterChange('heightTo', value)}
            onSliderChange={getOnSliderChange('heightFrom', 'heightTo')}
            formatter={getInputSuffixFormatter(t('glossary.measureMeter'))}
          />
        </Space>
      </Row>
    </Space>
  );
}

export default ComparativeAnalysisFilterPanel;
